import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';

import { Application, HttpStatusResult, WITHOUT_EMIT_EVENT } from '@jct/core';
import {
  AntiforgeryService,
  AccountService,
  IdentifyModel,
  AuthenticatorStates,
  IdentifyMethod,
  RegistrationUserProfile,
} from '@jct/api';
import { StateFactory } from '@jct/ui';
import { applyIdentityKeyValidators, idNumberValidator } from '../../identity-form.utils';

@Component({
  selector: 'identify-form.page',
  templateUrl: './identify-form.html',
})
export class IdentifyForm implements OnInit {
  constructor(
    private sf: StateFactory,
    private fb: UntypedFormBuilder,
    private af: AntiforgeryService,
    private accountService: AccountService,
    private router: Router,
    private application: Application,
    private activatedRoute: ActivatedRoute,
    public userProfile: RegistrationUserProfile,
  ) {}

  loadState = this.sf.create();
  identifyState = this.sf.create();

  form = this.fb.group({
    key: ['', [Validators.required, idNumberValidator()]],
    method: [IdentifyMethod.IdNumber],
    passwordGeneratorRelativePath: [this.application.passwordGeneratorPath],
  });

  params: Params;

  get method() {
    return this.form.get('method').value;
  }

  private applyKeyValidators() {
    applyIdentityKeyValidators(this.form.get('key'), this.method);
  }

  async ngOnInit() {
    await this.loadState.inProcess();

    const result = await this.af.generateAsync();

    if (!result.succeeded) {
      this.loadState.failed(result);
      return;
    }

    this.params = this.activatedRoute.snapshot.queryParams;

    this.form.get('method').valueChanges.subscribe(() => {
      const keyControl = this.form.get('key');
      keyControl.reset('', { emitEvent: false });
      keyControl.markAsPristine();
      keyControl.markAsUntouched();
      this.applyKeyValidators();
      this.identifyState.clearError();
    });

    await this.loadState.completed();
  }

  async identify() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (!this.identifyState.isInProcess) {
      this.form.disable(WITHOUT_EMIT_EVENT);
      await this.identifyState.inProcess(10);

      const model = this.form.value as IdentifyModel;
      const result = await this.accountService.identify(model);

      if (result instanceof HttpStatusResult) {
        this.form.enable(WITHOUT_EMIT_EVENT);
        this.identifyState.failed(result);
        return;
      }

      const identityParams = {
        ...this.params,
        ...result,
        key: model.key,
        method: model.method,
      };

      switch (result.state) {
        case AuthenticatorStates.ShouldVerify:
          await this.router.navigate(['/start/verify'], {
            queryParams: identityParams,
            skipLocationChange: true,
          });
          await this.identifyState.completed();
          break;

        case AuthenticatorStates.ShouldRegister:
          await this.router.navigate(['/start/register'], {
            queryParams: identityParams,
            skipLocationChange: true,
          });
          await this.identifyState.completed();
          break;

        case AuthenticatorStates.ShouldConfirmByEmail:
          await this.router.navigate(['/start/confirm'], {
            queryParams: identityParams,
            skipLocationChange: true,
          });
          await this.identifyState.completed();
          break;
      }
    }
  }
}
